import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import FilterPanel from "@/pages/category/components/FilterPanel";
import { usePageMeta } from "@/lib/seo";
import {
  getGenresByType,
  getMoviesByType,
  isMediaType,
  mediaTypeLabel,
  mediaTypes,
  type MediaType,
} from "@/lib/media";

type SortKey = "latest" | "hot" | "score";

const PAGE_SIZE = 8;

const typeIntro: Record<MediaType, string> = {
  movie: "院线新片、作者电影与类型佳作，按题材、地区与年份自由组合筛选。",
  tv: "华语剧集与海外剧集同步收录，涵盖悬疑、古装、都市与治愈题材。",
  variety: "慢综艺、竞演、真人秀与文化访谈，找到适合当下的放松方式。",
};

const sortOptions: { key: SortKey; label: string; icon: string }[] = [
  { key: "latest", label: "上映日期", icon: "ri-calendar-line" },
  { key: "hot", label: "热门程度", icon: "ri-fire-line" },
  { key: "score", label: "评分最高", icon: "ri-star-line" },
];

export default function CategoryPage() {
  const { type: rawType } = useParams();
  const valid = isMediaType(rawType);
  const type: MediaType = valid ? (rawType as MediaType) : "movie";
  const label = mediaTypeLabel[type];

  usePageMeta({
    title: `${label} - 枫与铃影视推荐`,
    description: `枫与铃${label}频道，收录最新${label}作品推荐，可按类型、地区、年份多维筛选，并按上映日期、热度与评分排序。`,
    keywords: `枫与铃,${label}推荐,${label}排行榜,最新${label},影视资讯`,
  });

  const all = useMemo(() => getMoviesByType(type), [type]);
  const genreOptions = useMemo(() => getGenresByType(type), [type]);
  const regionOptions = useMemo(
    () => Array.from(new Set(all.map((item) => item.region))),
    [all]
  );
  const yearOptions = useMemo(
    () => Array.from(new Set(all.map((item) => item.year))).sort((a, b) => b - a),
    [all]
  );

  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setSelectedGenres([]);
    setSelectedRegions([]);
    setSelectedYears([]);
    setSortKey("latest");
    setPage(1);
    setDrawerOpen(false);
  }, [type]);

  const toggle = <T,>(list: T[], value: T, setter: (next: T[]) => void) => {
    const exists = list.includes(value);
    setter(exists ? list.filter((item) => item !== value) : [...list, value]);
    setPage(1);
  };

  const resetFilters = () => {
    setSelectedGenres([]);
    setSelectedRegions([]);
    setSelectedYears([]);
    setPage(1);
  };

  const filtered = useMemo(() => {
    const result = all.filter((movie) => {
      const genreMatch =
        selectedGenres.length === 0 || movie.genres.some((genre) => selectedGenres.includes(genre));
      const regionMatch =
        selectedRegions.length === 0 || selectedRegions.includes(movie.region);
      const yearMatch = selectedYears.length === 0 || selectedYears.includes(movie.year);
      return genreMatch && regionMatch && yearMatch;
    });

    return result.sort((a, b) => {
      if (sortKey === "hot") return b.hot - a.hot;
      if (sortKey === "score") return b.score - a.score;
      return a.releaseDate < b.releaseDate ? 1 : -1;
    });
  }, [all, selectedGenres, selectedRegions, selectedYears, sortKey]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const visible = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const activeFilterCount =
    selectedGenres.length + selectedRegions.length + selectedYears.length;

  const filterPanel = (
    <FilterPanel
      genres={genreOptions}
      regions={regionOptions}
      years={yearOptions}
      selectedGenres={selectedGenres}
      selectedRegions={selectedRegions}
      selectedYears={selectedYears}
      onToggleGenre={(value) => toggle(selectedGenres, value, setSelectedGenres)}
      onToggleRegion={(value) => toggle(selectedRegions, value, setSelectedRegions)}
      onToggleYear={(value) => toggle(selectedYears, value, setSelectedYears)}
      onClearGenres={() => {
        setSelectedGenres([]);
        setPage(1);
      }}
      onReset={resetFilters}
    />
  );

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main className="pt-[72px]">
        <section className="border-b border-background-200 bg-background-100/80">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
            <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
              <Link to="/" className="cursor-pointer hover:text-primary-600">
                首页
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line text-sm" />
              </span>
              <span className="text-foreground-800">{label}</span>
            </nav>

            <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
              <div>
                <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  {label}
                </h1>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
                  {typeIntro[type]}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {mediaTypes.map((item) => (
                  <Link
                    key={item}
                    to={`/category/${item}`}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                      item === type
                        ? "bg-primary-500 font-medium text-background-50"
                        : "border border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
                    }`}
                  >
                    {mediaTypeLabel[item]}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
          <div className="hidden md:block">{filterPanel}</div>

          <div className="mb-5 flex items-center justify-between gap-3 md:mt-5">
            <div className="flex items-center gap-2 text-sm text-foreground-600">
              <span>
                共 <strong className="font-heading text-primary-600">{filtered.length}</strong> 部作品
              </span>
              {activeFilterCount > 0 ? (
                <span className="hidden rounded bg-background-200 px-2 py-0.5 text-[11px] text-foreground-600 sm:inline">
                  {activeFilterCount} 个筛选条件
                </span>
              ) : null}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs text-foreground-700 md:hidden"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-filter-3-line text-sm" />
                </span>
                筛选
                {activeFilterCount > 0 ? (
                  <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] font-medium text-background-50">
                    {activeFilterCount}
                  </span>
                ) : null}
              </button>

              <div className="flex items-center gap-1 rounded-md bg-background-100 p-1">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => {
                      setSortKey(option.key);
                      setPage(1);
                    }}
                    className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded px-2.5 py-1.5 text-xs transition-colors ${
                      sortKey === option.key
                        ? "bg-background-50 font-medium text-primary-600"
                        : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    <span className="hidden h-3.5 w-3.5 items-center justify-center sm:flex">
                      <i className={`${option.icon} text-sm`} />
                    </span>
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {visible.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4 xl:grid-cols-5">
              {visible.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          ) : (
            <EmptyState
              icon="ri-film-line"
              title="没有找到匹配的作品"
              description="试试减少筛选条件，或者换一个类型、地区与年份组合。"
              actionLabel="重置全部筛选"
              onAction={resetFilters}
            />
          )}

          {filtered.length > PAGE_SIZE ? (
            <div className="mt-10">
              <Pagination current={safePage} totalPages={totalPages} onChange={setPage} />
            </div>
          ) : null}
        </section>
      </main>

      {drawerOpen ? (
        <div className="fixed inset-0 z-[75] md:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 cursor-pointer bg-foreground-950/50"
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[82vh] overflow-y-auto rounded-t-lg bg-background-50">
            <div className="sticky top-0 flex items-center justify-between border-b border-background-200 bg-background-50 px-4 py-3">
              <h2 className="font-heading text-base font-semibold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="关闭"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-background-100 text-foreground-700"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="px-4 py-4">{filterPanel}</div>
            <div className="flex gap-3 border-t border-background-200 px-4 py-4">
              <button
                type="button"
                onClick={resetFilters}
                className="flex-1 cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-700"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex-1 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50"
              >
                查看 {filtered.length} 部作品
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <SiteFooter />
    </div>
  );
}